import { useMemo, useState, ReactNode } from 'react';
import {
  Flag,
  ShieldAlert,
  Search,
  X,
  Loader2,
  Eye,
  Ban,
  Eraser,
  Trash2,
  CheckCircle2,
} from 'lucide-react';
import { toast } from 'sonner';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import {
  useReportDetailQuery,
  useReportListQuery,
} from '@/client/api/messaging/messaging.query';
import { useResolveReportMutation } from '@/client/api/messaging/messaging.mutation';
import {
  formatEpoch,
  MessageReport,
  ReportListParams,
  ReportResolveAction,
  STATUS_BADGE,
} from '@/client/types/messaging';
import { useListFilters } from '@/client/hooks/useListFilters';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { useHasPermission } from '@/client/hooks/useHasPermission';
import Pagination from '@/client/components/common/Pagination';
import { generateIdempotencyKey } from '@/client/utils/requestSecurity';
import {
  OrgPartyLink,
  UserPartyLink,
} from '@/client/components/admin/Messaging/PartyHoverCard';

interface ReportFilters {
  status: string;
  sort: string;
  order: string;
}

const selectCls =
  'min-w-[130px] rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';

const AccessDenied = () => (
  <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-gray-100 bg-white py-20 text-center shadow-sm">
    <div className="flex h-14 w-14 items-center justify-center rounded-full bg-red-50">
      <ShieldAlert className="h-7 w-7 text-red-500" />
    </div>
    <h2 className="text-lg font-semibold text-gray-800">Access restricted</h2>
    <p className="max-w-sm text-sm text-gray-500">
      Viewing reports requires the{' '}
      <code className="rounded bg-gray-100 px-1.5 py-0.5 text-xs">message.view</code> permission.
    </p>
  </div>
);

const InfoRow = ({ label, value }: { label: string; value: ReactNode }) => (
  <div>
    <p className="mb-0.5 text-xs text-gray-500">{label}</p>
    <div className="break-all text-sm font-medium text-gray-900">{value ?? '—'}</div>
  </div>
);

const ReportDrawer = ({
  rid,
  isOpen,
  setIsOpen,
  canModerate,
  onClose,
}: {
  rid: string | null;
  isOpen: boolean;
  setIsOpen: (o: boolean) => void;
  canModerate: boolean;
  onClose: () => void;
}) => {
  const [notes, setNotes] = useState('');
  const { data: report, isLoading } = useReportDetailQuery(isOpen ? rid : null);
  const resolve = useResolveReportMutation();

  if (!isOpen) return null;

  const terminal =
    report?.status === 'actioned' || report?.status === 'dismissed';

  const act = async (action: ReportResolveAction, label: string) => {
    if (!rid) return;
    try {
      await resolve.mutateAsync({
        rid,
        action,
        notes: notes || undefined,
        idempotencyKey: generateIdempotencyKey(),
      });
      toast.success(label, { richColors: true });
      setNotes('');
      if (action === 'dismiss' || action === 'delete' || action === 'redact' || action === 'resolve') {
        onClose();
      }
    } catch (error: any) {
      toast.error(error?.message || `Failed: ${label}`, { richColors: true });
    }
  };

  return (
    <Drawer open={isOpen} onOpenChange={setIsOpen}>
      <DrawerContent className="mx-auto flex max-h-[92vh] max-w-2xl flex-col">
        <DrawerHeader className="border-b border-gray-100 text-left">
          <DrawerTitle className="flex items-center gap-2">
            <Flag className="h-5 w-5 text-amber-600" />
            {isLoading ? 'Loading…' : `Report ${rid?.slice(0, 8) ?? ''}…`}
          </DrawerTitle>
          <DrawerDescription>Moderation queue item</DrawerDescription>
        </DrawerHeader>

        <div className="flex-1 space-y-4 overflow-y-auto px-4 py-4">
          {isLoading && (
            <div className="space-y-3">
              <Skeleton className="h-8 w-40" />
              <Skeleton className="h-24 w-full" />
            </div>
          )}
          {!isLoading && report && (
            <>
              <span
                className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold uppercase ${
                  STATUS_BADGE[report.status ?? 'pending'] ?? STATUS_BADGE.pending
                }`}
              >
                {report.status ?? 'pending'}
              </span>
              <div className="grid grid-cols-2 gap-4">
                <InfoRow label="RID" value={report.rid} />
                <InfoRow label="Category" value={report.category ?? '—'} />
                <InfoRow label="Reason" value={report.reason ?? '—'} />
                <InfoRow
                  label="Reporter"
                  value={
                    report.reporter_uid != null ? (
                      <UserPartyLink uid={report.reporter_uid} summary={report.reporter_summary} />
                    ) : report.reporter_oid != null ? (
                      <OrgPartyLink oid={report.reporter_oid} summary={report.reporter_org_summary} />
                    ) : (
                      '—'
                    )
                  }
                />
                <InfoRow
                  label="Resolved by"
                  value={
                    report.resolved_by != null ? (
                      <UserPartyLink uid={report.resolved_by} summary={report.resolved_by_summary} />
                    ) : (
                      '—'
                    )
                  }
                />
                <InfoRow label="Message ref" value={report.message_ref ?? '—'} />
                <InfoRow label="Created" value={formatEpoch(report.created_at)} />
                <InfoRow
                  label="Conversation"
                  value={
                    <span className="font-mono text-xs">{report.conversation_key ?? '—'}</span>
                  }
                />
                <InfoRow label="Side effect" value={report.side_effect ?? '—'} />
              </div>
              {report.message_snapshot != null && (
                <div className="rounded-lg bg-gray-50 p-4">
                  <p className="text-xs text-gray-500">Message snapshot</p>
                  <pre className="mt-1 max-h-40 overflow-auto whitespace-pre-wrap text-xs text-gray-800">
                    {typeof report.message_snapshot === 'string'
                      ? report.message_snapshot
                      : JSON.stringify(report.message_snapshot, null, 2)}
                  </pre>
                </div>
              )}
              {canModerate && !terminal && (
                <div>
                  <label className="mb-1 block text-xs text-gray-500">Notes</label>
                  <input
                    type="text"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Optional resolution notes…"
                  />
                </div>
              )}
            </>
          )}
        </div>

        <DrawerFooter className="border-t border-gray-100">
          <div className="flex flex-wrap gap-2">
            {canModerate && report && !terminal && (
              <>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={resolve.isPending}
                  onClick={() => act('review', 'Marked reviewing')}
                >
                  {resolve.isPending ? (
                    <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                  ) : (
                    <Eye className="mr-1.5 h-4 w-4" />
                  )}
                  Review
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={resolve.isPending}
                  onClick={() => act('dismiss', 'Report dismissed')}
                >
                  <Ban className="mr-1.5 h-4 w-4" />
                  Dismiss
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={resolve.isPending}
                  onClick={() => act('redact', 'Message redacted')}
                >
                  <Eraser className="mr-1.5 h-4 w-4" />
                  Redact
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={resolve.isPending}
                  onClick={() => act('delete', 'Message soft-deleted')}
                >
                  <Trash2 className="mr-1.5 h-4 w-4" />
                  Soft-delete
                </Button>
                <Button
                  type="button"
                  size="sm"
                  disabled={resolve.isPending}
                  onClick={() => act('resolve', 'Report resolved')}
                >
                  <CheckCircle2 className="mr-1.5 h-4 w-4" />
                  Resolve
                </Button>
              </>
            )}
            <DrawerClose asChild>
              <Button type="button" variant="ghost" size="sm" onClick={onClose}>
                Close
              </Button>
            </DrawerClose>
          </div>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

const MessagingReportsPage = () => {
  useBreadcrumbs([
    { label: 'Communications', href: '/admin/communications/messaging' },
    { label: 'Messaging' },
    { label: 'Reports' },
  ]);

  const viewPerm = useHasPermission('message.view');
  const moderatePerm = useHasPermission('message.moderate');
  const canView = viewPerm.isSuperAdmin || viewPerm.hasPermission;
  const canModerate = moderatePerm.isSuperAdmin || moderatePerm.hasPermission;
  const permLoading = viewPerm.isLoading;

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const {
    searchTerm,
    debouncedSearchTerm,
    filters,
    page,
    limit,
    handleSearch,
    handleFilterChange,
    handlePageChange,
    handleLimitChange,
    resetFilters,
  } = useListFilters<ReportFilters>({
    initialFilters: { status: 'pending', sort: 'created_at', order: 'desc' },
    initialLimit: 25,
  });

  const listParams: ReportListParams = useMemo(() => {
    const q = debouncedSearchTerm.trim();
    return {
      limit,
      offset: (page - 1) * limit,
      sort: filters.sort || 'created_at',
      order: filters.order || 'desc',
      ...(filters.status ? { status: filters.status } : {}),
      ...(q
        ? q.includes(':')
          ? { conversation_key: q }
          : /^\d+$/.test(q)
            ? { reporter_uid: Number(q) }
            : { message_ref: q }
        : {}),
    };
  }, [debouncedSearchTerm, filters, page, limit]);

  const { data, isLoading, isError, error } = useReportListQuery(
    listParams,
    canView && !permLoading,
  );

  const reports = data?.reports ?? [];
  const pagination = data?.pagination;
  const forbidden =
    (error as { status?: number })?.status === 403 || (!permLoading && !canView);
  const hasFilters =
    !!searchTerm ||
    filters.status !== 'pending' ||
    filters.sort !== 'created_at' ||
    filters.order !== 'desc';

  if (permLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="mx-auto max-w-screen-2xl space-y-4">
          <Skeleton className="h-10 w-48" />
          <Skeleton className="h-24 w-full" />
        </div>
      </div>
    );
  }

  if (forbidden) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="mx-auto max-w-screen-2xl">
          <AccessDenied />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto max-w-screen-2xl space-y-6">
        <div>
          <div className="mb-1 flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-amber-500">
              <Flag className="h-4 w-4 text-white" />
            </div>
            <h1 className="text-2xl font-black text-gray-900">Reports</h1>
          </div>
          <p className="text-sm text-gray-500">
            Review flagged messages — dismiss, soft-delete, or redact in one step.
          </p>
        </div>

        <div className="rounded-lg bg-white p-4 shadow">
          <div className="flex flex-col gap-3 lg:flex-row lg:flex-wrap">
            <div className="relative min-w-[200px] flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Reporter UID, message ref, or conversation_key…"
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                className="w-full rounded-md border border-gray-300 py-2 pl-9 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className={selectCls}
            >
              <option value="">All statuses</option>
              <option value="pending">Pending</option>
              <option value="reviewing">Reviewing</option>
              <option value="actioned">Actioned</option>
              <option value="dismissed">Dismissed</option>
            </select>
            <select
              value={filters.sort}
              onChange={(e) => handleFilterChange('sort', e.target.value)}
              className={selectCls}
            >
              <option value="created_at">Sort: Created</option>
              <option value="updated_at">Sort: Updated</option>
              <option value="resolved_at">Sort: Resolved</option>
              <option value="status">Sort: Status</option>
            </select>
            <select
              value={filters.order}
              onChange={(e) => handleFilterChange('order', e.target.value)}
              className={selectCls}
            >
              <option value="desc">Newest first</option>
              <option value="asc">Oldest first</option>
            </select>
            {hasFilters && (
              <button
                type="button"
                onClick={resetFilters}
                className="flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50"
              >
                <X className="h-4 w-4" />
                Clear
              </button>
            )}
          </div>
        </div>

        {isError && !forbidden && (
          <div className="rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
            {(error as Error)?.message || 'Failed to load reports'}
          </div>
        )}

        <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100">
              <thead>
                <tr className="bg-gray-50">
                  {['Report', 'Category', 'Status', 'Reporter', 'Created'].map((h) => (
                    <th
                      key={h}
                      className="px-6 py-3.5 text-left text-xs font-semibold uppercase tracking-wider text-gray-500"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {isLoading &&
                  Array.from({ length: 8 }).map((_, i) => (
                    <tr key={i}>
                      {Array.from({ length: 5 }).map((__, j) => (
                        <td key={j} className="px-6 py-4">
                          <Skeleton className="h-4 w-20" />
                        </td>
                      ))}
                    </tr>
                  ))}
                {!isLoading && reports.length === 0 && (
                  <tr>
                    <td colSpan={5} className="py-16 text-center text-sm text-gray-500">
                      No reports found
                    </td>
                  </tr>
                )}
                {!isLoading &&
                  reports.map((r: MessageReport) => (
                    <tr
                      key={r.rid}
                      className="cursor-pointer hover:bg-gray-50"
                      onClick={() => {
                        setSelectedId(r.rid);
                        setIsDrawerOpen(true);
                      }}
                    >
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">
                          {r.reason || r.message_ref || r.rid}
                        </div>
                        <div className="mt-0.5 font-mono text-xs text-gray-400">
                          {r.rid.slice(0, 12)}…
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">{r.category ?? '—'}</td>
                      <td className="px-6 py-4">
                        <span
                          className={`rounded-full px-2.5 py-1 text-xs font-semibold uppercase ${
                            STATUS_BADGE[r.status ?? 'pending'] ?? STATUS_BADGE.pending
                          }`}
                        >
                          {r.status ?? 'pending'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm">
                        {r.reporter_uid != null ? (
                          <UserPartyLink uid={r.reporter_uid} summary={r.reporter_summary} />
                        ) : r.reporter_oid != null ? (
                          <OrgPartyLink oid={r.reporter_oid} summary={r.reporter_org_summary} />
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {formatEpoch(r.created_at)}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
          <div className="border-t border-gray-100 px-6 py-3">
            <Pagination
              total={pagination?.total ?? 0}
              page={page}
              itemsPerPage={limit}
              limit={limit}
              setPage={handlePageChange}
              setItemsPerPage={handleLimitChange}
              showButtons
              showSelect
              selectOptions={[10, 25, 50, 100]}
            />
          </div>
        </div>
      </div>

      <ReportDrawer
        rid={selectedId}
        isOpen={isDrawerOpen}
        setIsOpen={setIsDrawerOpen}
        canModerate={canModerate}
        onClose={() => {
          setIsDrawerOpen(false);
          setSelectedId(null);
        }}
      />
    </div>
  );
};

export default MessagingReportsPage;
